import { type RefObject } from 'react';
import { Box, Heading, Image, Badge, List, ListItem, Panel, PanelSection } from '@customerio/pluma-components/react';
import { FlowChart } from './flow-chart';
import * as styles from './styles.css';
import companyGrayIcon from './assets/company-gray.svg';
import accountIcon from './assets/account.svg';
import eventsIcon from './assets/events.svg';
import personGrayIcon from './assets/person-gray.svg';

type SimpleFlowCardProps = {
  flowId: string;
  flowTargets?: string;
  horizontalLine?: boolean;
  icon: string;
  label: string;
};

function SimpleFlowCard({ flowId, flowTargets, horizontalLine, icon, label }: SimpleFlowCardProps) {
  return (
    <ListItem
      className="js-flow-item"
      data-flow-id={flowId}
      data-flow-targets={flowTargets}
      data-flow-horizontal-line={horizontalLine === true ? 'true' : undefined}
    >
      <Panel>
        <PanelSection className={styles.panelSection}>
          <Image src={icon} alt="" className={styles.flowIcon} />
          {label}
        </PanelSection>
      </Panel>
    </ListItem>
  );
}

type FlowCardsProps = {
  containerRef: RefObject<HTMLDivElement | null>;
};

export function FlowCards({ containerRef }: FlowCardsProps) {
  return (
    <div ref={containerRef} className={styles.flowSection}>
      <FlowChart containerRef={containerRef} />
      <div className={styles.flowColumn}>
        <Heading level="2" mb="300" color="bold">
          Create custom objects
        </Heading>
        <List className={styles.flowList} styleType="none">
          <SimpleFlowCard
            flowId="companies"
            flowTargets="kathryn,enabled,orange;jacob,disabled,gray;wade,disabled,gray"
            icon={companyGrayIcon}
            label="Companies"
          />
          <SimpleFlowCard
            flowId="opportunities"
            flowTargets="kathryn,enabled,yellow;wade,disabled,gray"
            icon={accountIcon}
            label="Opportunities"
          />
          <SimpleFlowCard flowId="events" flowTargets="kathryn,enabled,blue" icon={eventsIcon} label="Events" />
        </List>
      </div>
      <div className={styles.flowSpacer} />
      <div className={styles.flowColumn}>
        <Heading level="2" mb="300" color="bold">
          Associate objects with people
        </Heading>
        <List className={styles.flowList} styleType="none">
          <SimpleFlowCard
            flowId="kathryn"
            flowTargets="template,enabled,purple,-9,-9;template,enabled,orange,-3,-3;template,enabled,yellow,3,3;template,enabled,blue,10,10"
            horizontalLine={true}
            icon={personGrayIcon}
            label="Kathryn Murphy"
          />
          <SimpleFlowCard flowId="jacob" icon={personGrayIcon} label="Jacob Jones" />
          <SimpleFlowCard flowId="wade" icon={personGrayIcon} label="Wade Warren" />
        </List>
      </div>
      <div className={styles.flowSpacer} />
      <div className={styles.flowColumn}>
        <Heading level="2" mb="300" color="bold">
          Utilize multi-object data
        </Heading>
        <List className={styles.flowList} styleType="none">
          <ListItem className="js-flow-item" data-flow-id="template">
            <Panel>
              <PanelSection p="200" textAlign="left">
                <Box mb="100">
                  Hey <Badge color="purple">customer.name</Badge>,
                </Box>
                <Box mb="100">
                  I saw that you registered for <Badge color="blue">event.name</Badge> and are attending on behalf of{' '}
                  <Badge color="clementine">company.name</Badge>.
                </Box>
                <Box>
                  If you're free, let's grab coffee and discuss any outstanding action items required before your trial
                  expires on <Badge color="yellow">opportunity.trial_end</Badge>.
                </Box>
              </PanelSection>
            </Panel>
          </ListItem>
        </List>
      </div>
    </div>
  );
}
